import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Save, ArrowLeft, Plus, X, GripVertical, Share2, QrCode, Copy } from 'lucide-react';
import { useAppEditor } from '../hooks/useAppEditor';
import { QRCodeCanvas } from 'qrcode.react';

const AppEditor = ({ supabase }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [showShareModal, setShowShareModal] = useState(false);

  const {
    formData,
    linkedScenarios,
    isLoading,
    isSaving,
    error,
    isEditing,
    updateFormField,
    saveApp,
    addScenario,
    removeScenario,
    reorderScenarios,
    getAvailableScenarios
  } = useAppEditor(supabase, id);

  const availableScenarios = getAvailableScenarios();

  // Form submission handler
  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      await saveApp();
      navigate('/apps');
    } catch (error) {
      console.error('Failed to save app:', error);
    }
  };

  // Cancel handler
  const handleCancel = () => {
    navigate('/apps');
  };

  // Add scenario handler
  const handleAddScenario = async (scenarioId) => {
    try {
      await addScenario(scenarioId);
    } catch (error) {
      console.error('Failed to add scenario:', error);
    }
  };

  // Remove scenario handler
  const handleRemoveScenario = async (linkId) => {
    try {
      await removeScenario(linkId);
    } catch (error) {
      console.error('Failed to remove scenario:', error);
    }
  };

  // Move scenario up
  const handleMoveUp = (index) => {
    if (index === 0) return;

    const newOrder = [...linkedScenarios];
    [newOrder[index - 1], newOrder[index]] = [newOrder[index], newOrder[index - 1]];
    reorderScenarios(newOrder);
  };

  // Move scenario down
  const handleMoveDown = (index) => {
    if (index === linkedScenarios.length - 1) return;

    const newOrder = [...linkedScenarios];
    [newOrder[index], newOrder[index + 1]] = [newOrder[index + 1], newOrder[index]];
    reorderScenarios(newOrder);
  };

  const handleCopyToClipboard = () => {
    const shareUrl = `https://pwa.qr2air.com?token=${formData.token}`;
    navigator.clipboard.writeText(shareUrl)
      .then(() => {
        console.log('URL copied to clipboard');
      })
      .catch(err => {
        console.error('Failed to copy URL: ', err);
      });
  };

  const downloadQRCode = () => {
    const canvas = document.getElementById('qr-code-canvas');
    if (canvas) {
      const pngUrl = canvas.toDataURL('image/png');
      const downloadLink = document.createElement('a');
      downloadLink.href = pngUrl;
      downloadLink.download = `${formData.name.replace(/\s+/g, '-')}-qr-code.png`;
      downloadLink.click();
    }
  };

  if (isLoading && !isSaving) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-gray-500">Loading app...</div>
      </div>
    );
  }

  return (
    <div className="bg-white shadow rounded-lg max-w-4xl mx-auto p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center">
          <button
            onClick={handleCancel}
            className="mr-3 text-gray-600 hover:text-gray-900"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <h2 className="text-xl font-semibold">
            {isEditing ? 'Edit App' : 'Create New App'}
          </h2>
        </div>

        {isEditing && formData.token && (
          <button
            onClick={() => setShowShareModal(true)}
            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
          >
            <Share2 className="h-4 w-4 mr-2" />
            Share
          </button>
        )}
      </div>

      {error && (
        <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-6">
          <div className="flex">
            <div className="ml-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="space-y-6">
          {/* Basic Information Section */}
          <div className="bg-white border rounded-lg p-6">
            <h3 className="text-lg font-medium mb-4">Basic Information</h3>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  App Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => updateFormField('name', e.target.value)}
                  className="w-full px-3 py-2 border rounded-md"
                  placeholder="My Event App"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => updateFormField('description', e.target.value)}
                  className="w-full px-3 py-2 border rounded-md"
                  rows="3"
                  placeholder="Check-in and registration for our event"
                />
              </div>

              {isEditing && formData.token && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Token
                  </label>
                  <input
                    type="text"
                    value={formData.token}
                    readOnly
                    className="w-full px-3 py-2 border rounded-md bg-gray-50"
                  />
                </div>
              )}
            </div>
          </div>

          {/* Branding Section */}
          <div className="bg-white border rounded-lg p-6">
            <h3 className="text-lg font-medium mb-4">Branding</h3>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Icon URL
                </label>
                <input
                  type="url"
                  value={formData.icon_url}
                  onChange={(e) => updateFormField('icon_url', e.target.value)}
                  className="w-full px-3 py-2 border rounded-md"
                  placeholder="https://example.com/icon-512x512.png"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Recommended: 512x512 PNG image for app icon
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Logo URL
                </label>
                <input
                  type="url"
                  value={formData.logo_url}
                  onChange={(e) => updateFormField('logo_url', e.target.value)}
                  className="w-full px-3 py-2 border rounded-md"
                  placeholder="https://example.com/logo.png"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Company or event logo displayed at the top of the app
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Theme Color
                  </label>
                  <div className="flex items-center">
                    <input
                      type="color"
                      value={formData.theme_color}
                      onChange={(e) => updateFormField('theme_color', e.target.value)}
                      className="h-10 w-20 border rounded-md cursor-pointer"
                    />
                    <input
                      type="text"
                      value={formData.theme_color}
                      onChange={(e) => updateFormField('theme_color', e.target.value)}
                      className="ml-2 flex-1 px-3 py-2 border rounded-md"
                      placeholder="#2563eb"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Background Color
                  </label>
                  <div className="flex items-center">
                    <input
                      type="color"
                      value={formData.background_color}
                      onChange={(e) => updateFormField('background_color', e.target.value)}
                      className="h-10 w-20 border rounded-md cursor-pointer"
                    />
                    <input
                      type="text"
                      value={formData.background_color}
                      onChange={(e) => updateFormField('background_color', e.target.value)}
                      className="ml-2 flex-1 px-3 py-2 border rounded-md"
                      placeholder="#ffffff"
                    />
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Branding Text
                </label>
                <input
                  type="text"
                  value={formData.branding_text}
                  onChange={(e) => updateFormField('branding_text', e.target.value)}
                  className="w-full px-3 py-2 border rounded-md"
                  placeholder="Powered by Company XYZ"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Footer text displayed at the bottom of the app
                </p>
              </div>
            </div>
          </div>

          {/* Scenarios Section */}
          <div className="bg-white border rounded-lg p-6">
            <h3 className="text-lg font-medium mb-4">Scenarios</h3>

            {!isEditing ? (
              <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
                <p className="text-sm text-blue-700">
                  Save the app first to add scenarios
                </p>
              </div>
            ) : (
              <>
                {/* Linked Scenarios */}
                {linkedScenarios.length > 0 && (
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Linked Scenarios ({linkedScenarios.length})
                    </label>
                    <div className="space-y-2">
                      {linkedScenarios.map((scenario, index) => (
                        <div
                          key={scenario.linkId}
                          className="flex items-center justify-between p-3 bg-gray-50 rounded-md"
                        >
                          <div className="flex items-center flex-1">
                            <div className="flex flex-col mr-2">
                              <button
                                type="button"
                                onClick={() => handleMoveUp(index)}
                                disabled={index === 0}
                                className="text-gray-400 hover:text-gray-600 disabled:opacity-30"
                              >
                                <GripVertical className="h-3 w-3" />
                              </button>
                              <button
                                type="button"
                                onClick={() => handleMoveDown(index)}
                                disabled={index === linkedScenarios.length - 1}
                                className="text-gray-400 hover:text-gray-600 disabled:opacity-30"
                              >
                                <GripVertical className="h-3 w-3" />
                              </button>
                            </div>
                            <div>
                              <div className="font-medium text-sm">{scenario.name}</div>
                              {scenario.description && (
                                <div className="text-xs text-gray-500">{scenario.description}</div>
                              )}
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => handleRemoveScenario(scenario.linkId)}
                            className="ml-3 text-red-600 hover:text-red-900"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Add Scenario Dropdown */}
                {availableScenarios.length > 0 && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Add Scenario
                    </label>
                    <select
                      onChange={(e) => {
                        if (e.target.value) {
                          handleAddScenario(e.target.value);
                          e.target.value = '';
                        }
                      }}
                      className="w-full px-3 py-2 border rounded-md"
                      defaultValue=""
                    >
                      <option value="" disabled>Select a scenario to add...</option>
                      {availableScenarios.map((scenario) => (
                        <option key={scenario.id} value={scenario.id}>
                          {scenario.name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {linkedScenarios.length === 0 && availableScenarios.length === 0 && (
                  <div className="bg-gray-50 border border-gray-200 rounded-md p-4">
                    <p className="text-sm text-gray-600">
                      No scenarios available. Create scenarios first, then add them to this app.
                    </p>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Save Button */}
          <div className="pt-4 flex justify-end space-x-3">
            <button
              type="button"
              onClick={handleCancel}
              className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900"
              disabled={isSaving}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              disabled={isSaving}
            >
              <Save className="h-4 w-4 mr-2" />
              {isSaving ? 'Saving...' : 'Save App'}
            </button>
          </div>
        </div>
      </form>

      {/* Share Modal */}
      {showShareModal && isEditing && formData.token && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg max-w-md w-full">
            <h3 className="text-lg font-semibold mb-4">Share App: {formData.name}</h3>

            {/* QR Code */}
            <div className="flex justify-center mb-4">
              <QRCodeCanvas
                id="qr-code-canvas"
                value={`https://pwa.qr2air.com?token=${formData.token}`}
                size={200}
                level="H"
                includeMargin={true}
              />
            </div>

            {/* URL */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Share URL
              </label>
              <input
                type="text"
                readOnly
                value={`https://pwa.qr2air.com?token=${formData.token}`}
                className="w-full px-3 py-2 border rounded-md"
                onClick={(e) => e.target.select()}
              />
            </div>

            {/* Buttons */}
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowShareModal(false)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
              >
                Close
              </button>
              <button
                onClick={downloadQRCode}
                className="inline-flex items-center px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700"
              >
                <QrCode className="h-4 w-4 mr-2" />
                Download QR
              </button>
              <button
                onClick={handleCopyToClipboard}
                className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
              >
                <Copy className="h-4 w-4 mr-2" />
                Copy URL
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AppEditor;
